import { Component, effect, inject, OnInit } from '@angular/core';
import { GlobalsStore } from '../../../stores/globals.state';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { MenuItem } from 'primeng/api';
import { Commune, ScatterConfiguration } from '../../../models/commune';
import { CheckboxModule } from 'primeng/checkbox';
import { DividerModule } from 'primeng/divider';
import { DropdownModule } from 'primeng/dropdown';
import { FormFieldWrapperComponent } from '../form-field-wrapper/form-field-wrapper.component';
import { NgIf } from '@angular/common';
import { RadioButtonModule } from 'primeng/radiobutton';
import { NgxSliderComponent } from '../../../ngx-slider/ngx-slider.component';
import { HeaderComponent } from '../../atoms/header/header.component';
import { CommuneStore } from '../../../stores/commune/commune.state';

@Component({
  selector: 'app-configuration-chart-scatter',
  standalone: true,
  imports: [
    CheckboxModule,
    DividerModule,
    DropdownModule,
    FormFieldWrapperComponent,
    FormsModule,
    NgIf,
    RadioButtonModule,
    ReactiveFormsModule,
    NgxSliderComponent,
    HeaderComponent,
  ],
  templateUrl: './configuration-chart-scatter.component.html',
  styleUrl: './configuration-chart-scatter.component.scss',
})
export class ConfigurationChartScatterComponent implements OnInit {
  readonly communeStore = inject(CommuneStore);
  readonly globalStore = inject(GlobalsStore);
  form: FormGroup = new FormGroup({});
  initialized = false;
  items: MenuItem[] = [];
  activeCommune: Commune | null = null;
  scatterConfiguration?: ScatterConfiguration;

  constructor(private fb: FormBuilder) {
    effect(() => {
      this.communeSetup();
    });
  }

  /**
   * Initializes the commune setup by assigning the active commune from the global store
   * and setting up its configuration if not already present. It ensures scatter configuration
   * is set based on the commune's atlas configuration and initializes the form if necessary.
   *
   * @return {void} This method does not return a value.
   */
  communeSetup(): void {
    if (!this.activeCommune) {
      this.activeCommune = this.globalStore.activeCommune();
      if (!this.activeCommune) return;
      if (!this.activeCommune.atlasConfig) {
        this.activeCommune.atlasConfig = this.communeStore.baseConfig();
      }
      this.scatterConfiguration = this.activeCommune.atlasConfig.scatter;
    }
    if (this.activeCommune && !this.initialized) {
      this.initForm();
    }
  }

  /**
   * Angular lifecycle hook that is called after the component's constructor
   * and the first ngOnChanges method, and is used for initializing the component.
   *
   * @return {void} This method does not return any value.
   */
  ngOnInit(): void {
    this.communeSetup();
  }

  /**
   * Initializes the form and sets up its fields and default values.
   * Subscribes to form value changes to trigger the submit method when the form values are updated.
   * Sets the initialized flag to true after the form has been set up.
   *
   * @return {void} Does not return a value.
   */
  initForm(): void {
    this.form = this.fb.group({
      dotSize: [this.scatterConfiguration?.dotSize || 3],
      displayRegression: [
        this.scatterConfiguration?.displayRegression || false,
      ],
    });

    this.form.valueChanges.subscribe(() => {
      this.submit();
    });

    this.initialized = true;
  }

  /**
   * Submits the form data and updates the active commune's scatter configuration.
   * If there is no active commune, the method returns without performing any action.
   *
   * @return {void} Does not return any value.
   */
  submit(): void {
    if (!this.activeCommune) return;

    this.activeCommune.atlasConfig.scatter = {
      ...this.activeCommune.atlasConfig.scatter,
      ...this.form.value,
    };
    this.communeStore.update(this.activeCommune);
  }
}
